import { useQuery } from '@tanstack/react-query';
import { fetchAddressSuggestions, type AddressSuggestion } from '../api/geocodingApi';
import { useDebouncedValue } from './useDebouncedValue';

const MIN_CHARS = 3;
const DEBOUNCE_MS = 300;

export type SuggestionsState = 'idle' | 'loading' | 'ok' | 'unavailable' | 'failed';

// Keyed on the settled text, so a late answer never replaces a newer input (AC19).
// No retry: every call spends quota.
export const useAddressSuggestions = (input: string, country: string | null, enabled: boolean) => {
  const text = input.trim();
  const settled = useDebouncedValue(text, DEBOUNCE_MS);

  const active = enabled && text.length >= MIN_CHARS;
  const { data, isError } = useQuery({
    queryKey: ['geocoding', 'autocomplete', settled, country],
    queryFn: () => fetchAddressSuggestions(settled, country),
    enabled: active && settled === text,
    retry: false,
    staleTime: 5 * 60_000,
  });

  const state: SuggestionsState = !active
    ? 'idle'
    : settled !== text
      ? 'loading'
      : isError
        ? 'failed'
        : data?.status ?? 'loading';
  const suggestions: AddressSuggestion[] = state === 'ok' ? data?.suggestions ?? [] : [];
  return { state, suggestions };
};
